/**
 * Le configurazioni degli apparati: gli apparati, la loro storia, e il testo.
 *
 * ## Perché si parte dagli apparati e non dalle modifiche
 *
 * Un elenco di modifiche risponde a «cosa è cambiato stanotte», ed è la domanda
 * del mattino. Non risponde a «com'è messo lo switch di Milano», che è la
 * domanda di quando qualcosa non va — e con quaranta apparati diventa centinaia
 * di righe in cui quella domanda non ha una riga a cui corrispondere.
 *
 * Le due viste stanno quindi affiancate, e si parte dagli apparati: da lì si
 * scende alla storia, e da una revisione si vede sia **cosa è cambiato** sia
 * **la configurazione intera**. La seconda mancava del tutto: si poteva leggere
 * la differenza e non il testo, cioè si poteva sapere che una riga era comparsa
 * e non cosa c'era intorno.
 */
import { useState } from 'react'

import { api } from '../api/client'
import type { ConfigRevision } from '../api/types'
import { Card, ErrorBanner, Spinner } from '../components/common'
import { Confronto, Intera } from '../components/ConfigViste'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

type Apparato = {
  ci_id: string
  ci_name: string | null
  address: string
  family: string
  transport: string
  revisions: number
  last_change: string | null
}

export default function DeviceConfigs() {
  const elenco = useAsync(() => api.configDevices(), [])
  const [scelto, setScelto] = useState<Apparato | null>(null)
  const [errore, setErrore] = useState<unknown>(null)

  if (elenco.error) return <ErrorBanner error={elenco.error} />
  if (elenco.loading && !elenco.data) return <Spinner />

  const apparati = elenco.data?.devices ?? []
  const doppioni = elenco.data?.duplicate_addresses ?? []

  async function dimentica(a: Apparato) {
    if (
      !window.confirm(
        t('Cancellare tutta la storia di «{nome}»?', { nome: a.ci_name ?? a.address }) +
          '\n\n' +
          t('Sono {n} versioni, e non si recuperano. Serve quando un apparato è stato dismesso: la sua configurazione non interessa più, e tenerla in elenco fa cercare fra apparati che non esistono.', { n: a.revisions }),
      )
    )
      return
    setErrore(null)
    try {
      await api.deleteConfigHistory(a.ci_id)
      if (scelto?.ci_id === a.ci_id) setScelto(null)
      elenco.reload()
    } catch (err) {
      setErrore(err)
    }
  }

  const colonne: Column<Apparato>[] = [
    { key: 'ci_name', header: t('Apparato'), value: (a) => a.ci_name ?? a.ci_id },
    { key: 'address', header: t('Indirizzo'), mono: true },
    {
      key: 'letto',
      header: t('Letto come'),
      value: (a) => `${a.family} ${a.transport}`,
      render: (a) => (
        <>
          <span className="badge">{a.family}</span>{' '}
          <span className="badge badge--info">{a.transport}</span>
        </>
      ),
    },
    { key: 'revisions', header: t('Versioni'), type: 'number' },
    {
      key: 'last_change',
      header: t('Ultima modifica'),
      type: 'date',
      render: (a) => (a.last_change ? new Date(a.last_change).toLocaleString(localeCorrente()) : '—'),
    },
    {
      key: 'azioni',
      header: '',
      sort: false,
      searchable: false,
      render: (a) => (
        <div className="rowactions">
          <button type="button" className="link" onClick={() => setScelto(scelto?.ci_id === a.ci_id ? null : a)}>
            {scelto?.ci_id === a.ci_id ? t('Chiudi') : t('Storia')}
          </button>{' '}
          <button type="button" className="link" onClick={() => void dimentica(a)}>
            {t('Elimina')}
          </button>
        </div>
      ),
    },
  ]

  return (
    <>
      <ErrorBanner error={errore} />

      <Conservazione />

      {/* Due CI sullo stesso indirizzo sono lo stesso apparato visto due volte,
          e in console si legge come «questo switch ha due configurazioni» —
          con la causa invisibile. Unirli è una decisione sulla CMDB, quindi si
          dice invece di deciderlo qui. */}
      {doppioni.length > 0 && (
        <div className="banner banner--warn">
          <strong>{t('Due oggetti in CMDB per lo stesso indirizzo.')}</strong>{' '}
          {t('È lo stesso apparato visto due volte: la configurazione si salva una volta sola, ma finché restano separati la scheda mostra due storie.')}
          <ul style={{ margin: '6px 0 0', paddingLeft: '1.2em' }}>
            {doppioni.map((d) => (
              <li key={d} className="mono">
                {d}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Card title={t('Apparati con configurazione salvata')}>
        {apparati.length === 0 ? (
          <p className="muted">
            {t('Nessuna configurazione salvata. Si comincia con un job di tipo «Backup configurazioni» sugli apparati di rete.')}
          </p>
        ) : (
          <DataTable
            rows={apparati}
            columns={colonne}
            rowKey={(a) => a.ci_id}
            conteggio={{ nome: t('apparati') }}
            isSelected={(a) => scelto?.ci_id === a.ci_id}
          />
        )}
      </Card>

      {scelto && <Storia apparato={scelto} />}
    </>
  )
}

/**
 * Per quanto si conserva la storia.
 *
 * ## Perché il predefinito è «tutto»
 *
 * Cancellare la storia di una configurazione è irreversibile, e una politica
 * accesa di serie la cancellerebbe **prima** che qualcuno sappia che esiste: al
 * primo giro dell'orchestratore dopo un aggiornamento, su tutto il parco.
 *
 * ## Perché si dice quante ne toglierebbe
 *
 * Perché fra impostare un numero e sapere cosa comporta c'è la differenza fra
 * una scelta e una scommessa. Senza, si scopre di aver cancellato quattromila
 * revisioni dal contatore del giorno dopo.
 */
/**
 * Come si salvano le configurazioni: di impianto, non per job.
 *
 * NETCONF e mascheramento erano opzioni della singola scansione — e per
 * giunta non arrivavano: lo schema le scartava in silenzio, e la sonda andava
 * sempre di default. La scelta è di impianto: deciderla diversamente in due
 * job produrrebbe un archivio incoerente.
 */
function ImpostazioniBackup() {
  const stato = useAsync(() => api.backupSettings(), [])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function cambia(netconf: boolean, mask: boolean) {
    setBusy(true)
    setError(null)
    try {
      await api.setBackupSettings(netconf, mask)
      await stato.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const s = stato.data
  return (
    <Card title={t('Come si salva')}>
      <ErrorBanner error={error ?? stato.error} />
      {!s ? (
        <Spinner />
      ) : (
        <>
          <div className="checkbox">
            <input
              id="imp-netconf"
              type="checkbox"
              checked={s.netconf}
              disabled={busy}
              onChange={(e) => cambia(e.target.checked, s.mask_secrets)}
            />
            <label htmlFor="imp-netconf">
              {t('Prova NETCONF prima della CLI')}
              <small>
                {t('Quando disponibile, acquisisce la configurazione in formato strutturato e senza paginazione; in caso contrario, utilizza automaticamente la CLI. Disabilita questa modalità solo se il dispositivo presenta problemi durante il tentativo di acquisizione.')}
              </small>
            </label>
          </div>
          <div className="checkbox">
            <input
              id="imp-mask"
              type="checkbox"
              checked={s.mask_secrets}
              disabled={busy}
              onChange={(e) => cambia(s.netconf, e.target.checked)}
            />
            <label htmlFor="imp-mask">
              {t('Escludi le credenziali dall’archiviazione')}
              <small>
                {t('Password, chiavi e community vengono escluse dall’archiviazione per evitare la conservazione permanente delle credenziali. Una configurazione salvata potrà essere ripristinata unicamente reinserendo manualmente le credenziali.')}
              </small>
            </label>
          </div>
          <p className="field__hint">
            {t('Vale per tutti i salvataggi, dei job e delle pianificazioni: una scelta sola, un archivio coerente.')}
          </p>
        </>
      )}
    </Card>
  )
}


function Conservazione() {
  const politica = useAsync(() => api.configRetention(), [])
  const [versioni, setVersioni] = useState<string | null>(null)
  const [giorni, setGiorni] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  const vers = versioni ?? (politica.data?.max_versions?.toString() ?? '')
  const gg = giorni ?? (politica.data?.max_days?.toString() ?? '')
  const cambiato = versioni !== null || giorni !== null

  async function salva() {
    setBusy(true)
    setErrore(null)
    try {
      await api.setConfigRetention({
        max_versions: vers.trim() === '' ? null : Number(vers),
        max_days: gg.trim() === '' ? null : Number(gg),
      })
      setVersioni(null)
      setGiorni(null)
      politica.reload()
    } catch (err) {
      setErrore(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
    <ImpostazioniBackup />
    <Card title={t('Politica di Retention dello storico')}>
      <ErrorBanner error={errore} />
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Le regole di retention sono cumulative: una versione viene eliminata solo quando non soddisfa più alcun criterio di conservazione. L’ultima versione della configurazione di ogni asset è sempre esclusa dalla cancellazione, indipendentemente dalle regole configurate.')}{' '}
        {t('L’ultima versione di ogni apparato non si cancella mai, qualunque cosa dicano le regole: un archivio senza la configurazione corrente è un backup senza backup.')}
      </p>

      <div className="grid grid--2">
        <div className="field">
          <label htmlFor="ret-versioni">{t('Versioni per apparato')}</label>
          <input
            id="ret-versioni"
            type="number"
            min={1}
            max={10000}
            placeholder={t('tutte')}
            value={vers}
            onChange={(e) => setVersioni(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="ret-giorni">{t('Giorni')}</label>
          <input
            id="ret-giorni"
            type="number"
            min={1}
            max={3650}
            placeholder={t('per sempre')}
            value={gg}
            onChange={(e) => setGiorni(e.target.value)}
          />
        </div>
      </div>

      {(politica.data?.would_remove ?? 0) > 0 && (
        <div className="banner banner--warn">
          {t('Con questa politica verranno tolte {n} versioni al prossimo giro dell’orchestratore. Non succede al salvataggio: c’è il tempo di accorgersi di aver scritto 3 dove si voleva 30.', { n: politica.data?.would_remove })}
        </div>
      )}

      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button onClick={salva} disabled={busy || !cambiato}>
          {busy ? t('Salvataggio…') : t('Salva')}
        </button>
      </div>
    </Card>
    </>
  )
}

/** Le revisioni di un apparato, dalla più recente. */
function Storia({ apparato }: { apparato: Apparato }) {
  const revisioni = useAsync(
    () => api.configRevisions(apparato.ci_id),
    [apparato.ci_id],
  )
  const [aperta, setAperta] = useState<ConfigRevision | null>(null)
  const [errore, setErrore] = useState<unknown>(null)

  async function elimina(r: ConfigRevision) {
    if (
      !window.confirm(
        t('Cancellare la versione del {data}?', {
          data: new Date(r.collected_at).toLocaleString(localeCorrente()),
        }) +
          '\n\n' +
          t('Non si recupera. Il confronto delle versioni rimaste continuerà a funzionare, ma salterà quello che è successo in mezzo.'),
      )
    )
      return
    setErrore(null)
    try {
      await api.deleteConfigRevision(r.id)
      if (aperta?.id === r.id) setAperta(null)
      revisioni.reload()
    } catch (err) {
      setErrore(err)
    }
  }

  if (revisioni.error) return <ErrorBanner error={revisioni.error} />

  const colonne: Column<ConfigRevision>[] = [
    {
      key: 'collected_at',
      header: t('Quando'),
      type: 'date',
      render: (r) => new Date(r.collected_at).toLocaleString(localeCorrente()),
    },
    {
      key: 'transport',
      header: t('Letta come'),
      render: (r) => <span className="badge badge--info">{r.transport}</span>,
    },
    {
      key: 'pulizia',
      header: t('Pulizia'),
      value: (r) => t('{righe} righe volatili · {segreti} segreti', { righe: r.lines_removed, segreti: r.secrets_masked }),
      render: (r) => (
        <span className="muted">
          {t('{righe} righe volatili · {segreti} segreti', { righe: r.lines_removed, segreti: r.secrets_masked })}
        </span>
      ),
    },
    { key: 'size', header: t('Dimensione'), type: 'number', render: (r) => `${(r.size / 1024).toFixed(1)} kB` },
    {
      key: 'azioni',
      header: '',
      sort: false,
      searchable: false,
      render: (r) => (
        <div className="rowactions">
          <button type="button" className="link" onClick={() => setAperta(aperta?.id === r.id ? null : r)}>
            {aperta?.id === r.id ? t('Chiudi') : t('Apri')}
          </button>{' '}
          <button type="button" className="link" onClick={() => void elimina(r)}>
            {t('Elimina')}
          </button>
        </div>
      ),
    },
  ]

  return (
    <>
      <Card title={t('Storia di {nome}', { nome: apparato.ci_name ?? apparato.address })}>
        <ErrorBanner error={errore} />
        {revisioni.loading ? (
          <Spinner />
        ) : (
          <DataTable
            rows={revisioni.data ?? []}
            columns={colonne}
            rowKey={(r) => r.id}
            initialSort={{ key: 'collected_at', dir: 'desc' }}
            conteggio={{ nome: t('versioni') }}
            isSelected={(r) => aperta?.id === r.id}
          />
        )}
      </Card>

      {aperta && (
        <Revisione
          revisione={aperta}
          nome={apparato.ci_name ?? apparato.address}
        />
      )}
    </>
  )
}

/**
 * Una revisione: cosa è cambiato, e la configurazione intera.
 *
 * Due schede e non due pagine: sono la stessa domanda a due profondità — «cosa
 * è successo» e «com'è fatto adesso» — e chi guarda la prima passa quasi sempre
 * alla seconda.
 */
function Revisione({
  revisione,
  nome,
}: {
  revisione: ConfigRevision
  nome: string
}) {
  const [scheda, setScheda] = useState<'diff' | 'intera'>('diff')

  return (
    <Card
      title={`${nome} — ${new Date(revisione.collected_at).toLocaleString(localeCorrente())}`}
      actions={
        <div className="rowactions">
          <button
            type="button"
            className={scheda === 'diff' ? '' : 'secondary'}
            onClick={() => setScheda('diff')}
          >
            {t('Cosa è cambiato')}
          </button>{' '}
          <button
            type="button"
            className={scheda === 'intera' ? '' : 'secondary'}
            onClick={() => setScheda('intera')}
          >
            {t('Configurazione intera')}
          </button>
        </div>
      }
    >
      {scheda === 'diff' ? (
        <Confronto revisione={revisione} />
      ) : (
        <Intera revisione={revisione} nome={nome} />
      )}
    </Card>
  )
}

